/**
 * Display-only date helpers — use in UI labels/tables, not in save/update/delete payloads.
 * API responses must stay in raw UTC format for booking and status operations.
 */
import dayjs, { Dayjs } from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import {
  formatLocalDate as formatLocalDateBase,
  formatLocalDateTime as formatLocalDateTimeBase,
  formatLocalTime as formatLocalTimeBase,
  parseToLocal as parseToLocalBase,
  toUtcIso as toUtcIsoBase,
  toUtcZ,
  LOCAL_DATE_FORMAT,
  LOCAL_DATETIME_FORMAT,
  LOCAL_TIME_FORMAT,
} from "../methods/localDateTime";

dayjs.extend(customParseFormat);

const inputFormats = [
  "YYYY-MM-DD",
  "YYYY-MM-DD HH:mm:ss",
  "YYYY-MM-DD hh:mm A",
  "YYYY-MM-DDTHH:mm:ss",
  "YYYY-MM-DDTHH:mm:ss.SSS",
  "YYYY-MM-DDTHH:mm:ss.SSSZ",
  "DD/MM/YYYY",
  "DD/MM/YYYY HH:mm:ss",
  "DD/MM/YYYY hh:mm A",
  "MM/DD/YYYY",
  "MM/DD/YYYY HH:mm:ss",
  "MM/DD/YYYY hh:mm A",
  "DD-MM-YYYY",
  "DD-MM-YYYY HH:mm:ss",
  "DD-MM-YYYY hh:mm A",
  "MM-DD-YYYY",
  "MM-DD-YYYY HH:mm:ss",
  "MM-DD-YYYY hh:mm A",
];

export {
  LOCAL_DATE_FORMAT,
  LOCAL_TIME_FORMAT,
  LOCAL_DATETIME_FORMAT,
  toUtcZ,
};

export function parseToLocal(
  value: string | number | Date | Dayjs | null | undefined,
): Dayjs | null {
  return parseToLocalBase(value);
}

export function formatLocalDate(
  value: string | number | Date | Dayjs | null | undefined,
  format = LOCAL_DATE_FORMAT,
  fallback = "-",
): string {
  return formatLocalDateBase(value, format, fallback);
}

export function formatLocalTime(
  value: string | number | Date | Dayjs | null | undefined,
  format = LOCAL_TIME_FORMAT,
  fallback = "-",
): string {
  return formatLocalTimeBase(value, format, fallback);
}

export function formatLocalDateTime(
  value: string | number | Date | Dayjs | null | undefined,
  format = LOCAL_DATETIME_FORMAT,
  fallback = "-",
): string {
  return formatLocalDateTimeBase(value, format, fallback);
}

export function toUtcIso(
  value: string | number | Date | Dayjs | null | undefined,
): string | null {
  return toUtcIsoBase(value);
}

export function parseFlexibleDate(value: string | null | undefined) {
  if (!value) return null;
  const local = parseToLocal(value);
  if (local) return local;
  const parsed = dayjs(value, inputFormats, true);
  return parsed.isValid() ? parsed : null;
}

export function formatFlexibleDate(
  value: string | null | undefined,
  displayFormat = LOCAL_DATE_FORMAT,
) {
  return formatLocalDate(value, displayFormat, value?.trim() ? value : "-");
}

/**
 * @deprecated Do not use on API responses or mutation payloads.
 * Prefer formatLocalDate / formatLocalDateTime at the UI layer only.
 */
export function normalizeDates<T = any>(
  data: T,
  targetFormat = LOCAL_DATETIME_FORMAT,
): T {
  if (data == null) return data;

  if (typeof data === "string") {
    const formatted = formatLocalDateTime(data, targetFormat, "");
    return (formatted || data) as any;
  }

  if (Array.isArray(data))
    return data.map((v) => normalizeDates(v, targetFormat)) as T;

  if (typeof data === "object") {
    return Object.fromEntries(
      Object.entries(data as Record<string, any>).map(([k, v]) => [
        k,
        normalizeDates(v, targetFormat),
      ]),
    ) as T;
  }

  return data;
}
